import { useState, useEffect, useCallback, useMemo } from 'react';
import { colaboradoresService } from '../services/colaboradores.service';
import { resultadosService } from '../services/resultados.service';
import type { CandidatoPendenteContract } from '@sstexames/shared-types';
import { formatCPF } from '../utils/formatters';
import { getHojeISO } from '../utils/dateUtils';
import {
  CheckBadgeIcon,
  XMarkIcon,
  CheckIcon,
  DocumentArrowDownIcon,
  ClockIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserMinusIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

export default function CandidatosAprovacao() {
  const [candidatos, setCandidatos] = useState<CandidatoPendenteContract[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'pendente_aprovacao' | 'aprovado' | 'reprovado' | 'desistencia' | 'todos'>('pendente_aprovacao');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedMes, setSelectedMes] = useState<string>('todos');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;

  // Modal Actions
  const [selectedCandidate, setSelectedCandidate] = useState<CandidatoPendenteContract | null>(null);
  const [actionType, setActionType] = useState<'aprovar' | 'reprovar' | null>(null);
  const [dataAdmissaoInput, setDataAdmissaoInput] = useState<string>('');
  const [matriculaEsocialInput, setMatriculaEsocialInput] = useState<string>('');
  const [enviarEsocialInput, setEnviarEsocialInput] = useState<boolean>(true);
  const [reprovacaoTipo, setReprovacaoTipo] = useState<'reprovado' | 'desistencia'>('reprovado');
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [actionSuccessMessage, setActionSuccessMessage] = useState<string>('');

  const loadCandidatos = useCallback(async () => {
    try {
      setLoading(true);
      const statusParam = activeTab === 'todos' ? undefined : activeTab;
      const data = await colaboradoresService.findCandidatos(statusParam);
      setCandidatos(data || []);
    } catch (error) {
      console.error('Erro ao carregar candidatos:', error);
      setCandidatos([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadCandidatos();
  }, [loadCandidatos]);

  // Lista dinâmica de meses disponíveis nos registros para o filtro
  const mesesDisponiveis = useMemo(() => {
    const setMeses = new Set<string>();
    for (const c of candidatos) {
      const dtStr = c.data_exame || c.created_at;
      if (dtStr) {
        const dt = new Date(dtStr);
        if (!isNaN(dt.getTime())) {
          const chave = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
          setMeses.add(chave);
        }
      }
    }
    return Array.from(setMeses).sort().reverse();
  }, [candidatos]);

  const handleOpenAprovar = (cand: CandidatoPendenteContract) => {
    setSelectedCandidate(cand);
    setActionType('aprovar');
    setDataAdmissaoInput(cand.data_admissao || getHojeISO());
    setMatriculaEsocialInput(cand.matricula_esocial || '');
    setEnviarEsocialInput(true);
    setActionSuccessMessage('');
  };

  const handleOpenReprovar = (cand: CandidatoPendenteContract) => {
    setSelectedCandidate(cand);
    setActionType('reprovar');
    setReprovacaoTipo('reprovado');
    setActionSuccessMessage('');
  };

  const handleConfirmAction = async () => {
    if (!selectedCandidate || !actionType) return;
    try {
      setActionLoading(true);
      if (actionType === 'aprovar') {
        const res = await colaboradoresService.aprovarCandidato(selectedCandidate.id, {
          data_admissao: dataAdmissaoInput,
          matricula_esocial: matriculaEsocialInput,
          enviar_esocial: enviarEsocialInput,
        });
        setActionSuccessMessage(res.message || 'Candidato aprovado com sucesso! S-2220 liberado.');
      } else {
        const res = await colaboradoresService.reprovarCandidato(selectedCandidate.id, {
          tipo: reprovacaoTipo,
        });
        setActionSuccessMessage(res.message || 'Ação registrada com sucesso.');
      }
      setTimeout(() => {
        setSelectedCandidate(null);
        setActionType(null);
        setActionSuccessMessage('');
        loadCandidatos();
      }, 1500);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      alert(err?.response?.data?.message || err?.message || 'Erro ao processar ação.');
    } finally {
      setActionLoading(false);
    }
  };

  const [downloadingAsoId, setDownloadingAsoId] = useState<number | null>(null);

  const handleDownloadAso = async (cand: CandidatoPendenteContract) => {
    if (!cand.resultado_id && !cand.aso_storage_path) {
      alert('Arquivo do ASO não disponível.');
      return;
    }
    try {
      setDownloadingAsoId(cand.id);
      let blob: Blob;
      if (cand.resultado_id) {
        try {
          blob = await resultadosService.downloadResultado(cand.resultado_id);
        } catch (resErr) {
          console.warn('Falha no download via resultado_id, tentando fallback por candidato:', resErr);
          blob = await colaboradoresService.downloadCandidatoAso(cand.id);
        }
      } else {
        blob = await colaboradoresService.downloadCandidatoAso(cand.id);
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cleanName = cand.nome.replace(/[^a-zA-Z0-9]/g, '_');
      a.download = `ASO_${cleanName}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Erro ao baixar ASO:', error);
      alert('Não foi possível realizar o download do ASO no momento.');
    } finally {
      setDownloadingAsoId(null);
    }
  };

  // Filtragem combinada: busca + mês
  const filteredCandidatos = useMemo(() => {
    return candidatos.filter((c) => {
      // Filtro de mês
      if (selectedMes !== 'todos') {
        const dtStr = c.data_exame || c.created_at;
        if (!dtStr) return false;
        const dt = new Date(dtStr);
        if (isNaN(dt.getTime())) return false;
        const chave = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
        if (chave !== selectedMes) return false;
      }

      // Filtro de texto
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        c.nome.toLowerCase().includes(term) ||
        (c.cpf && c.cpf.includes(term)) ||
        (c.cargo_nome && c.cargo_nome.toLowerCase().includes(term)) ||
        (c.cbo && c.cbo.includes(term))
      );
    });
  }, [candidatos, selectedMes, searchTerm]);

  // Paginação
  const totalPages = Math.max(1, Math.ceil(filteredCandidatos.length / itemsPerPage));
  const paginatedCandidatos = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCandidatos.slice(start, start + itemsPerPage);
  }, [filteredCandidatos, currentPage, itemsPerPage]);

  const formatMesLabel = (chave: string) => {
    const [ano, mes] = chave.split('-');
    const dt = new Date(parseInt(ano), parseInt(mes) - 1, 1);
    return dt.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  const getAsoStatusBadge = (status?: string | null) => {
    switch (status) {
      case 'apto':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">
            Apto
          </span>
        );
      case 'apto_com_restricao':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-teal-100 text-teal-800 border border-teal-300">
            Apto c/ Restrição
          </span>
        );
      case 'laudo_emitido':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-sky-100 text-sky-800 border border-sky-300">
            Laudo Emitido
          </span>
        );
      case 'em_auditoria':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
            Em Auditoria
          </span>
        );
      case 'inapto':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-300">
            Inapto
          </span>
        );
      case 'cancelado':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-300">
            Cancelado
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300">
            Aguardando ASO
          </span>
        );
    }
  };

  const getCandidatoStatusBadge = (status: string) => {
    switch (status) {
      case 'aprovado':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
            Contratação Aprovada
          </span>
        );
      case 'reprovado':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
            Reprovado
          </span>
        );
      case 'desistencia':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
            Desistência
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
            Pendente de Decisão
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2 text-sm text-amber-700 font-medium mb-1">
              <CheckBadgeIcon className="w-5 h-5 text-amber-600" />
              <span>Admissões e eSocial S-2220</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
              Aprovação de Candidatos
            </h1>
            <p className="mt-1 text-sm md:text-base text-gray-500 max-w-3xl">
              Consulte os ASOs admissionais realizados. Ao aprovar uma contratação, o sistema vincula o colaborador e autoriza a transmissão do evento <strong>S-2220</strong> ao eSocial.
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => loadCandidatos()}
              disabled={loading}
              className="inline-flex items-center space-x-2 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-2xs"
            >
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin text-amber-600' : 'text-gray-500'}`} />
              <span>Atualizar</span>
            </button>
          </div>
        </div>

        {/* Tabs de Status */}
        <div className="flex border-b border-gray-200 mt-6 -mb-2 overflow-x-auto">
          <button
            onClick={() => { setActiveTab('pendente_aprovacao'); setCurrentPage(1); }}
            className={`py-3 px-4 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'pendente_aprovacao'
                ? 'border-amber-600 text-amber-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Pendentes de Decisão
          </button>
          <button
            onClick={() => { setActiveTab('aprovado'); setCurrentPage(1); }}
            className={`py-3 px-4 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'aprovado'
                ? 'border-amber-600 text-amber-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Contratações Aprovadas
          </button>
          <button
            onClick={() => { setActiveTab('reprovado'); setCurrentPage(1); }}
            className={`py-3 px-4 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'reprovado'
                ? 'border-amber-600 text-amber-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Reprovados
          </button>
          <button
            onClick={() => { setActiveTab('desistencia'); setCurrentPage(1); }}
            className={`py-3 px-4 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'desistencia'
                ? 'border-amber-600 text-amber-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Desistências
          </button>
          <button
            onClick={() => { setActiveTab('todos'); setCurrentPage(1); }}
            className={`py-3 px-4 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'todos'
                ? 'border-amber-600 text-amber-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Todos os Candidatos
          </button>
        </div>
      </div>

      {/* Barra de Filtros (Busca + Filtro por Mês) */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="relative flex-1 w-full">
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Buscar por nome, CPF, cargo ou CBO..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors"
            />
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto">
            <CalendarDaysIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <select
              value={selectedMes}
              onChange={(e) => { setSelectedMes(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 bg-white"
            >
              <option value="todos">Todos os Meses</option>
              {mesesDisponiveis.map((m) => (
                <option key={m} value={m}>
                  {formatMesLabel(m)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Content List / Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mb-3" />
            <p className="text-sm text-gray-500">Carregando candidatos e exames...</p>
          </div>
        ) : filteredCandidatos.length === 0 ? (
          <div className="p-12 text-center">
            <CheckBadgeIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-gray-900">Nenhum candidato encontrado</h3>
            <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
              Não há candidatos com os filtros selecionados no momento.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50/80 text-gray-600 font-semibold">
                <tr>
                  <th className="py-3.5 px-4">Candidato</th>
                  <th className="py-3.5 px-4">Cargo / CBO</th>
                  <th className="py-3.5 px-4">Clínica Credenciada</th>
                  <th className="py-3.5 px-4">Data do Exame</th>
                  <th className="py-3.5 px-4">Resultado ASO</th>
                  <th className="py-3.5 px-4">Laudo (ASO)</th>
                  <th className="py-3.5 px-4">Status Decisão</th>
                  <th className="py-3.5 px-4 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-700">
                {paginatedCandidatos.map((cand) => (
                  <tr key={cand.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="py-4 px-4">
                      <div className="font-semibold text-gray-900">{cand.nome}</div>
                      <div className="text-xs text-gray-500">CPF: {formatCPF(cand.cpf)}</div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-gray-900 font-medium">{cand.cargo_nome || 'Não definido'}</div>
                      <div className="text-xs text-gray-500 font-mono">
                        {cand.cbo ? `CBO ${cand.cbo}` : cand.setor_nome || '-'}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-1.5 text-gray-900">
                        <BuildingOfficeIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="truncate max-w-[180px]">{cand.clinica_nome || 'A definir via link'}</span>
                      </div>
                      {cand.clinica_cidade && (
                        <div className="text-xs text-gray-500 ml-5">{cand.clinica_cidade}</div>
                      )}
                    </td>
                    <td className="py-4 px-4 whitespace-nowrap">
                      {cand.data_exame ? (
                        <div className="flex items-center space-x-1 text-gray-800">
                          <ClockIcon className="w-3.5 h-3.5 text-gray-400" />
                          <span>{new Date(cand.data_exame + 'T12:00:00Z').toLocaleDateString('pt-BR')}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Pendente</span>
                      )}
                    </td>
                    <td className="py-4 px-4 whitespace-nowrap">
                      {getAsoStatusBadge(cand.aso_status)}
                    </td>
                    <td className="py-4 px-4 whitespace-nowrap">
                      {cand.aso_storage_path || cand.resultado_id ? (
                        <button
                          type="button"
                          onClick={() => handleDownloadAso(cand)}
                          disabled={downloadingAsoId === cand.id}
                          className="inline-flex items-center space-x-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-300 rounded-lg text-xs font-semibold hover:bg-emerald-100 transition-colors shadow-2xs disabled:opacity-50 cursor-pointer"
                          title="Baixar laudo digital do ASO em PDF"
                        >
                          <DocumentArrowDownIcon className="w-4 h-4 text-emerald-600" />
                          <span>{downloadingAsoId === cand.id ? 'Baixando...' : 'Baixar ASO'}</span>
                        </button>
                      ) : cand.aso_status === 'cancelado' ? (
                        <span
                          className="inline-flex items-center space-x-1 text-gray-500 text-xs bg-gray-50 px-2 py-1 rounded border border-gray-200"
                          title="O exame foi cancelado e não haverá emissão de ASO."
                        >
                          <XCircleIcon className="w-3.5 h-3.5 text-gray-400" />
                          <span>Cancelado</span>
                        </span>
                      ) : cand.aso_status === 'em_auditoria' ? (
                        <span
                          className="inline-flex items-center space-x-1 text-indigo-700 text-xs font-medium bg-indigo-50 px-2 py-1 rounded border border-indigo-200"
                          title="O laudo foi anexado pela clínica credenciada e está em validação médica na mesa de auditoria."
                        >
                          <ClockIcon className="w-3.5 h-3.5 text-indigo-600" />
                          <span>Em Auditoria</span>
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center space-x-1 text-gray-400 text-xs italic bg-gray-50 px-2 py-1 rounded border border-gray-200"
                          title="A clínica credenciada ainda não disponibilizou o laudo digital do ASO."
                        >
                          <ClockIcon className="w-3.5 h-3.5 text-amber-500" />
                          <span>Aguardando emissão</span>
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-4 whitespace-nowrap">
                      {getCandidatoStatusBadge(cand.candidato_status)}
                    </td>
                    <td className="py-4 px-4 text-right whitespace-nowrap">
                      {cand.candidato_status === 'pendente_aprovacao' ? (
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleOpenAprovar(cand)}
                            className="inline-flex items-center space-x-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-500 shadow-2xs transition-colors"
                          >
                            <CheckIcon className="w-3.5 h-3.5" />
                            <span>Aprovar</span>
                          </button>
                          <button
                            onClick={() => handleOpenReprovar(cand)}
                            className="inline-flex items-center space-x-1 px-3 py-1.5 border border-red-200 text-red-700 hover:bg-red-50 rounded-lg text-xs font-medium transition-colors"
                          >
                            <XMarkIcon className="w-3.5 h-3.5" />
                            <span>Reprovar</span>
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Concluído</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                <p className="text-xs text-gray-500">
                  Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filteredCandidatos.length)} de {filteredCandidatos.length} candidatos
                </p>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  <span className="text-xs font-semibold text-gray-700">
                    {currentPage} de {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {selectedCandidate && actionType && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center space-x-3">
              <div
                className={`p-3 rounded-xl border ${
                  actionType === 'aprovar'
                    ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                    : 'bg-red-50 text-red-600 border-red-200'
                }`}
              >
                {actionType === 'aprovar' ? (
                  <CheckIcon className="w-6 h-6" />
                ) : (
                  <ExclamationTriangleIcon className="w-6 h-6" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {actionType === 'aprovar'
                    ? 'Confirmar Aprovação de Contratação'
                    : 'Registrar Reprovação ou Desistência'}
                </h3>
                <p className="text-xs text-gray-500">
                  {selectedCandidate.nome} • CPF: {formatCPF(selectedCandidate.cpf)}
                  {selectedCandidate.cbo ? ` • CBO: ${selectedCandidate.cbo}` : ''}
                </p>
              </div>
            </div>

            {actionSuccessMessage ? (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-sm font-semibold text-center">
                {actionSuccessMessage}
              </div>
            ) : (
              <>
                {actionType === 'aprovar' ? (
                  <div className="space-y-4 text-sm text-gray-600">
                    <p className="leading-relaxed">
                      Ao aprovar esta contratação, o candidato é admitido como colaborador ativo da empresa e o evento de Saúde Ocupacional <strong>S-2220</strong> será liberado para transmissão ao <strong>eSocial</strong>.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-gray-100">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          Data de Admissão Efectiva *
                        </label>
                        <input
                          type="date"
                          value={dataAdmissaoInput}
                          onChange={(e) => setDataAdmissaoInput(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          Matrícula eSocial
                        </label>
                        <input
                          type="text"
                          value={matriculaEsocialInput}
                          onChange={(e) => setMatriculaEsocialInput(e.target.value)}
                          placeholder="Ex: 001234"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                      </div>
                    </div>

                    <div className="p-3.5 bg-emerald-50/70 border border-emerald-200 rounded-xl space-y-1">
                      <label className="flex items-start gap-3 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={enviarEsocialInput}
                          onChange={(e) => setEnviarEsocialInput(e.target.checked)}
                          className="w-4 h-4 mt-0.5 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                        />
                        <div>
                          <span className="text-xs font-bold text-gray-900 block">
                            Confirmar envio ao e-Social (Recomendado: Sim)
                          </span>
                          <span className="text-[11px] text-gray-600 leading-relaxed block mt-0.5">
                            Habilita a transmissão dos eventos de SST (S-2220 para ASO e S-2240 para Condições Ambientais de Trabalho) para este colaborador admitido.
                          </span>
                        </div>
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 text-sm text-gray-600">
                    <p className="leading-relaxed">
                      Selecione o motivo do encerramento. <strong>Nenhum evento</strong> será transmitido ao eSocial e não é necessário digitar justificativa formal.
                    </p>

                    <div className="space-y-2 pt-2">
                      <label
                        className={`flex items-center p-3 border rounded-xl cursor-pointer transition-colors ${
                          reprovacaoTipo === 'reprovado'
                            ? 'border-red-500 bg-red-50/50 text-red-900 font-semibold'
                            : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="tipo"
                          value="reprovado"
                          checked={reprovacaoTipo === 'reprovado'}
                          onChange={() => setReprovacaoTipo('reprovado')}
                          className="text-red-600 focus:ring-red-500 mr-3"
                        />
                        <div>
                          <p className="text-sm">Reprovação Clínica / Não Apto</p>
                          <p className="text-xs text-gray-500 font-normal">Candidato reprovado no exame admissional ou no processo seletivo.</p>
                        </div>
                      </label>

                      <label
                        className={`flex items-center p-3 border rounded-xl cursor-pointer transition-colors ${
                          reprovacaoTipo === 'desistencia'
                            ? 'border-gray-500 bg-gray-50 text-gray-900 font-semibold'
                            : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="tipo"
                          value="desistencia"
                          checked={reprovacaoTipo === 'desistencia'}
                          onChange={() => setReprovacaoTipo('desistencia')}
                          className="text-gray-600 focus:ring-gray-500 mr-3"
                        />
                        <div className="flex items-center gap-2">
                          <UserMinusIcon className="w-4 h-4 text-gray-500" />
                          <div>
                            <p className="text-sm">Desistência do Candidato</p>
                            <p className="text-xs text-gray-500 font-normal">O candidato desistiu da vaga antes da contratação efetiva.</p>
                          </div>
                        </div>
                      </label>
                    </div>

                    {selectedCandidate?.has_agendamento_pendente && (
                      <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3 text-amber-800">
                        <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                        <div className="text-xs leading-relaxed">
                          <p className="font-semibold text-amber-900">Agendamento ativo detectado</p>
                          <p className="mt-0.5 text-amber-800">
                            Este candidato possui agendamento de exame pendente ou em andamento. Ao confirmar a {reprovacaoTipo === 'desistencia' ? 'desistência' : 'reprovação'}, <strong>o agendamento será cancelado automaticamente</strong> no sistema e a clínica será avisada.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => {
                      setSelectedCandidate(null);
                      setActionType(null);
                    }}
                    disabled={actionLoading}
                    className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleConfirmAction}
                    disabled={actionLoading}
                    className={`px-5 py-2 rounded-xl text-sm font-semibold text-white shadow-xs transition-colors flex items-center space-x-2 ${
                      actionType === 'aprovar'
                        ? 'bg-emerald-600 hover:bg-emerald-500'
                        : 'bg-red-600 hover:bg-red-500'
                    }`}
                  >
                    {actionLoading ? (
                      <span>Processando...</span>
                    ) : actionType === 'aprovar' ? (
                      <span>Sim, Aprovar e Liberar S-2220</span>
                    ) : selectedCandidate?.has_agendamento_pendente ? (
                      <span>Confirmar {reprovacaoTipo === 'desistencia' ? 'Desistência' : 'Reprovação'} e Cancelar Agendamento</span>
                    ) : reprovacaoTipo === 'desistencia' ? (
                      <span>Confirmar Desistência</span>
                    ) : (
                      <span>Confirmar Reprovação</span>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
